/**
 * @expFlag 3promo-enabled-public-animated-trend-modal
 * @ticket CLOUDWEB-20130
 * @file
 */
/* eslint-disable max-lines-per-function */
import { Button, ButtonGroup, Spacing, Subhead, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';

import { PublicAnimatedTrendFullscreen } from './blocks/PublicAnimatedTrendFullscreen';
import styles from './PublicAnimatedTrendModal.css';

export interface IPublicAnimatedTrendModalProps {
    theme: 'blue' | 'light-blue';
    title: string;
    subtitle: string;
    buttons: Array<{
        onClick?(): void;
        appearance: 'accent' | 'neutral' | 'overlay';
        text: string;
        href?: string;
        qaId?: string;
    }>;
    onShow(): void;
    onClose(): void;
    onXClose(): void;
    onTapOutsideClose(): void;
    isFullscreen?: boolean;
    closable?: boolean;
    imageSrc: string;
}

export const PublicAnimatedTrendModal = memo<IPublicAnimatedTrendModalProps>(
    ({
        theme,
        title,
        subtitle,
        buttons,
        onClose,
        onShow,
        onXClose,
        onTapOutsideClose,
        closable = true,
        imageSrc,
        isFullscreen = false,
    }) => {
        const scrollRef = useRef<HTMLDivElement>(null);
        const isBlueTheme = theme === 'blue';

        useEffect(() => {
            onShow();
        }, []);

        const handleButtonClick = useCallback((button: { onClick?(): void }) => {
            button.onClick?.();
            onClose();
        }, []);

        if (isFullscreen) {
            return (
                <PublicAnimatedTrendFullscreen
                    theme={theme}
                    title={title}
                    subtitle={subtitle}
                    buttons={buttons}
                    onXCloseAnalytics={onXClose}
                    onClose={onClose}
                    closable={closable}
                    imageSrc={imageSrc}
                    handleButtonClick={handleButtonClick}
                />
            );
        }

        return (
            <MobileDialog
                id="public-benefits-auth-mob"
                onClose={onClose}
                onDimmerClick={onTapOutsideClose}
                onCloseButtonClick={onXClose}
                topmost
                closeOnDimmerClick={closable}
                closable={closable}
                scrollRef={scrollRef}
                mod="base"
                closeMode={isBlueTheme ? 'lightAndRound' : 'darkAndRound'}
                contextPadding={'zero'}
                className={classNames({
                    [styles.mainMobileDialog]: true,
                    [styles.blueDialog]: theme === 'blue',
                    [styles.lightBlueDialog]: theme === 'light-blue',
                })}
            >
                <div ref={scrollRef} className={styles.rootMobile}>
                    <div className={styles.imageContainer}>
                        <img src={imageSrc} className={styles.image} />
                    </div>
                    <div className={styles.contentContainer}>
                        <Title
                            level="2"
                            className={classNames({
                                [styles.title]: true,
                                [styles.title_color]: isBlueTheme,
                            })}
                            data-qa-id="public-benefits-auth-mob-header"
                        >
                            {title}
                        </Title>
                        <Subhead
                            weight="3"
                            className={classNames({
                                [styles.subhead]: true,
                                [styles.subhead_color]: isBlueTheme,
                            })}
                            data-qa-id="public-benefits-auth-mob-content"
                        >
                            {subtitle}
                        </Subhead>
                        <Spacing size={24} />
                        <ButtonGroup mode="vertical" gap="m" stretched>
                            {buttons.map((button, index) => (
                                <Button
                                    key={index}
                                    size="l"
                                    mode="primary"
                                    appearance={button.appearance}
                                    onClick={() => handleButtonClick(button)}
                                    stretched
                                    Component={button.href ? 'a' : 'button'}
                                    href={`${button.href}?mt_deeplink=${window.location.href}`}
                                    className={classNames({
                                        [styles.button_accent_black_color]: button.appearance === 'accent' && isBlueTheme,
                                    })}
                                    data-qa-id={button.qaId}
                                >
                                    {button.text}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>
                </div>
            </MobileDialog>
        );
    }
);

PublicAnimatedTrendModal.displayName = 'PublicAnimatedTrendModal';
